/**
 * Wiki display formatting, matching the prototype's strings:
 *   dates        "Oct 2", "Sep 18" (year added only when it isn't this year), "Just now" (< 1 min)
 *   relative     "just now", "4 min ago", "1 h ago", then the date label
 *   day + time   "Today, 9:12", "Yesterday, 16:40", "Oct 2, 9:12"
 *   versions     "v4"
 *   paths        "Sales › Pricing" (U+203A with spaces)
 *   plurals      "1 page", "3 pages" (the prototype printed "1 pages" — fixed here)
 */

import { plural } from '../../../lib/plural';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Parse an API timestamp. A naive ISO string (no `Z`/offset) is read as UTC —
 * the runtime stores UTC and some serializers drop the offset (memory:
 * "Naive Mongo datetime wire trap").
 */
export function parseWikiDate(value) {
    if (value == null || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') {
        const fromNumber = new Date(value);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }
    let text = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text)) text += 'Z';
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toNow(now) {
    return now instanceof Date ? now : new Date(now ?? Date.now());
}

function sameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "Oct 2" — "Oct 2, 2025" for another year — "Just now" under a minute. '' when unparseable. */
export function dateLabel(value, now) {
    const date = parseWikiDate(value);
    if (!date) return '';
    const current = toNow(now);
    const diff = current.getTime() - date.getTime();
    if (diff >= 0 && diff < MINUTE) return 'Just now';
    const base = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
    return date.getFullYear() === current.getFullYear() ? base : `${base}, ${date.getFullYear()}`;
}

/** "just now", "4 min ago", "1 h ago", "3 d ago" (< 7 days), else `dateLabel`. */
export function relTime(value, now) {
    const date = parseWikiDate(value);
    if (!date) return '';
    const current = toNow(now);
    const diff = current.getTime() - date.getTime();
    if (diff < MINUTE) return 'just now';
    if (diff < HOUR) return `${Math.floor(diff / MINUTE)} min ago`;
    if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`;
    if (diff < 7 * DAY) return `${Math.floor(diff / DAY)} d ago`;
    return dateLabel(date, current);
}

function clock(date) {
    return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** "Today, 9:12" / "Yesterday, 16:40" / "Oct 2, 9:12". */
export function dayTimeLabel(value, now) {
    const date = parseWikiDate(value);
    if (!date) return '';
    const current = toNow(now);
    if (sameDay(date, current)) return `Today, ${clock(date)}`;
    const yesterday = new Date(current.getFullYear(), current.getMonth(), current.getDate() - 1);
    if (sameDay(date, yesterday)) return `Yesterday, ${clock(date)}`;
    const base = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
    const withYear = date.getFullYear() === current.getFullYear() ? base : `${base} ${date.getFullYear()}`;
    return `${withYear}, ${clock(date)}`;
}

export { plural };   // one implementation, shared with the admin Pipelines page: lib/plural.js

/** versionLabel(4) → "v4"; '' when there is no version. */
export function versionLabel(version) {
    if (version == null || version === '') return '';
    return `v${version}`;
}

/** wikiPathLabel('Sales', 'Pricing') → "Sales › Pricing"; just the category without a sub. */
export function wikiPathLabel(categoryName, subcategoryName) {
    const parts = [categoryName, subcategoryName].filter((part) => part != null && part !== '');
    return parts.join(' › ');
}

/**
 * The New-category palette (prototype logic ~739,654): colour → tint pairs.
 * The first pair is the default.
 */
export const WIKI_CATEGORY_PALETTE = Object.freeze([
    Object.freeze({ color: '#0F766E', tint: '#CCFBF1' }),
    Object.freeze({ color: '#B45309', tint: '#FEF3C7' }),
    Object.freeze({ color: '#0E7490', tint: '#CFFAFE' }),
    Object.freeze({ color: '#6D28D9', tint: '#EDE9FE' }),
    Object.freeze({ color: '#BE185D', tint: '#FCE7F3' }),
    Object.freeze({ color: '#334155', tint: '#E2E8F0' }),
    Object.freeze({ color: '#15803D', tint: '#DCFCE7' }),
]);

/**
 * `{ color, tint }` for a category: its own values, else the palette tint for
 * its colour, else the default teal pair.
 */
export function categoryColors(category) {
    const color = typeof category?.color === 'string' && category.color ? category.color : null;
    if (color && category?.tint) return { color, tint: category.tint };
    const pair = color && WIKI_CATEGORY_PALETTE.find((p) => p.color.toLowerCase() === color.toLowerCase());
    if (pair) return { color: pair.color, tint: pair.tint };
    if (color) return { color, tint: `color-mix(in srgb, ${color} 14%, #fff)` };
    return { ...WIKI_CATEGORY_PALETTE[0] };
}
